import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
} from "@angular/forms";
import { EventsTableComponent } from "@shared/components/events-table/events-table.component";
import { PaginatorState } from "primeng/paginator";
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
} from "rxjs";
import { EventsManagementService } from "../../../../services/events-management.service";
import {
  EventStatusFilter,
  MyEventsFilters,
} from "../../../../services/types/events-management-request.type";

// Validador de grupo: faturamento mínimo não pode ser maior que o máximo.
function revenueRangeValidator(
  group: AbstractControl,
): ValidationErrors | null {
  const min = group.get("minRevenue")?.value;
  const max = group.get("maxRevenue")?.value;

  if (
    min !== null &&
    min !== undefined &&
    max !== null &&
    max !== undefined &&
    Number(min) > Number(max)
  ) {
    return { revenueRange: true };
  }

  return null;
}

@Component({
  selector: "app-my-events-list-container",
  imports: [CommonModule, ReactiveFormsModule, EventsTableComponent],
  templateUrl: "./my-events-list-container.component.html",
  styleUrl: "./my-events-list-container.component.scss",
})
export class MyEventsListContainerComponent {
  private readonly eventsService = inject(EventsManagementService);
  private readonly page$ = new BehaviorSubject<PaginatorState>({
    page: 0,
    rows: 5,
  });

  readonly filtersForm = new FormGroup(
    {
      query: new FormControl<string>("", { nonNullable: true }),
      status: new FormControl<"" | EventStatusFilter>("", {
        nonNullable: true,
      }),
      minRevenue: new FormControl<number | null>(null),
      maxRevenue: new FormControl<number | null>(null),
    },
    { validators: revenueRangeValidator },
  );

  myEvents$ = combineLatest([
    this.page$,
    this.filtersForm.valueChanges.pipe(
      debounceTime(400),
      startWith(null),
      map(() => this.buildFilters()),
      distinctUntilChanged(
        (previous, current) =>
          JSON.stringify(previous) === JSON.stringify(current),
      ),
    ),
  ]).pipe(
    switchMap(([page, filters]) =>
      this.eventsService.getMyEvents(page.page, page.rows, filters),
    ),
  );

  get hasActiveFilters(): boolean {
    return Object.keys(this.buildFilters()).length > 0;
  }

  get hasRevenueRangeError(): boolean {
    return this.filtersForm.hasError("revenueRange");
  }

  handlePageChange(page: PaginatorState) {
    this.page$.next(page);
  }

  // Bloqueia notação científica e sinais que o input type="number" aceita por
  // padrão (e, E, +, -), impedindo valores como "--0987891023e".
  blockInvalidNumberKeys(event: KeyboardEvent): void {
    if (["e", "E", "+", "-"].includes(event.key)) {
      event.preventDefault();
    }
  }

  clearFilters(): void {
    this.filtersForm.reset({
      query: "",
      status: "",
      minRevenue: null,
      maxRevenue: null,
    });
  }

  private buildFilters(): MyEventsFilters {
    const { query, status, minRevenue, maxRevenue } =
      this.filtersForm.getRawValue();
    const filters: MyEventsFilters = {};

    if (query?.trim()) {
      filters.query = query.trim();
    }
    if (status) {
      filters.status = status;
    }
    // Faixa inválida (mín. > máx.) não é enviada ao backend.
    if (!this.filtersForm.hasError("revenueRange")) {
      if (this.isValidRevenue(minRevenue)) {
        filters.min_revenue = minRevenue;
      }
      if (this.isValidRevenue(maxRevenue)) {
        filters.max_revenue = maxRevenue;
      }
    }

    return filters;
  }

  private isValidRevenue(value: number | null): value is number {
    return value !== null && Number.isFinite(value) && value >= 0;
  }
}
